/**
 * Maps a `task_error` WebSocket event to the TaskProgress entry consumers see
 * (async reliability lane). The backend sends a static English `error` plus,
 * when known, the stable `error_code` and its `error_message_key`
 * (`jobs.errors.<code>`); both are passed through so progress screens can
 * show the translated message instead of the English text.
 */
import type { TaskProgress } from './websocket/types';

export interface TaskErrorPayload {
  error?: string | null;
  error_code?: string | null;
  error_message_key?: string | null;
}

export function taskErrorProgress(
  taskId: string,
  payload: TaskErrorPayload | null | undefined,
  existing?: TaskProgress | null,
): TaskProgress {
  // Always set: a task_error must never read as a successful completion.
  const error = payload?.error || 'Task failed';
  return {
    taskId,
    stage: 'error',
    progress: existing?.progress || 0,
    message: error,
    metadata: existing?.metadata,
    isComplete: true,
    error,
    ...(payload?.error_code ? { errorCode: payload.error_code } : {}),
    ...(payload?.error_message_key ? { errorMessageKey: payload.error_message_key } : {}),
  };
}

/** Error details handed to `useTaskProgress` onError consumers. */
export interface TaskErrorDetails {
  errorCode?: string;
  errorMessageKey?: string;
}

export function taskErrorDetails(progress: Pick<TaskProgress, 'errorCode' | 'errorMessageKey'>): TaskErrorDetails {
  return { errorCode: progress.errorCode, errorMessageKey: progress.errorMessageKey };
}
